import { userClient } from "@/shared/rpc/clients";
import type { UserSettingMessage } from "@cronika/proto/common/user_pb";
import { makeAutoObservable, runInAction } from "mobx";

class UserSettingsStore {
  loaded = false;
  loading = false;
  private entries = new Map<string, Map<string, UserSettingMessage>>();
  private loadPromise: Promise<void> | null = null;

  constructor() {
    makeAutoObservable(this, { loadPromise: false, entries: false } as any);
  }

  getSetting(section: string, key: string): UserSettingMessage | undefined {
    return this.entries.get(section)?.get(key);
  }

  setSetting(entry: UserSettingMessage) {
    const section = entry.section ?? "";
    let sectionMap = this.entries.get(section);
    if (!sectionMap) {
      sectionMap = new Map();
      this.entries.set(section, sectionMap);
    }
    sectionMap.set(entry.key, entry);
  }

  deleteSetting(section: string, key: string) {
    this.entries.get(section)?.delete(key);
  }

  load(): Promise<void> {
    if (this.loaded) return Promise.resolve();
    if (this.loadPromise) return this.loadPromise;

    runInAction(() => {
      this.loading = true;
    });

    this.loadPromise = userClient
      .listSettings({})
      .then((result) => {
        runInAction(() => {
          for (const entry of result.data ?? []) {
            this.setSetting(entry);
          }
          this.loaded = true;
        });
      })
      .catch(() => {
        // silent — hooks will use defaults
      })
      .finally(() => {
        runInAction(() => {
          this.loading = false;
        });
        // Allow retry on next call if request failed (loaded stays false)
        this.loadPromise = null;
      });

    return this.loadPromise;
  }
}

export const userSettingsStore = new UserSettingsStore();
